import type { DeuceRule } from '@/types/game-format';
import { DEFAULT_DEUCE_RULE } from '@/types/game-format';

/**
 * Determines whether a game has been won based on the scores and deuce rule.
 *
 * - win_by_2: At deuce (both >= target-1), need 2-point margin. Otherwise, first to target.
 * - sudden_death: First to target wins. No deuce extension.
 */
export function isGameWon(
  p1: number,
  p2: number,
  pointsPerGame: number,
  deuceRule: DeuceRule = DEFAULT_DEUCE_RULE,
): boolean {
  if (deuceRule === 'sudden_death') {
    return p1 >= pointsPerGame || p2 >= pointsPerGame;
  }

  // win_by_2
  if (p1 >= pointsPerGame - 1 && p2 >= pointsPerGame - 1) {
    // At deuce — need 2-point margin
    return Math.abs(p1 - p2) >= 2;
  }

  return p1 >= pointsPerGame || p2 >= pointsPerGame;
}

/**
 * Validates a completed game score for manual entry.
 * Returns whether the score is valid and who won.
 *
 * Rules:
 * - Exactly one player must have won (reached the target with proper margin)
 * - The loser's score must be consistent with the deuce rule
 */
export function validateGameScore(
  s1: number,
  s2: number,
  pointsPerGame: number,
  deuceRule: DeuceRule = DEFAULT_DEUCE_RULE,
): { valid: boolean; winner: 'player1' | 'player2' | null } {
  if (!Number.isFinite(s1) || !Number.isFinite(s2)) {
    return { valid: false, winner: null };
  }
  if (s1 < 0 || s2 < 0) {
    return { valid: false, winner: null };
  }
  if (s1 === s2) {
    return { valid: false, winner: null };
  }

  const hi = Math.max(s1, s2);
  const lo = Math.min(s1, s2);
  const winner = s1 > s2 ? 'player1' : 'player2';

  if (hi < pointsPerGame) {
    // Neither player reached the target
    return { valid: false, winner: null };
  }

  if (deuceRule === 'sudden_death') {
    // Winner must be exactly at the target, loser below
    if (hi !== pointsPerGame) {
      return { valid: false, winner: null };
    }
    return { valid: true, winner };
  }

  // win_by_2
  if (hi === pointsPerGame) {
    // Winner hit the target exactly — loser must be at most target - 2 (no deuce)
    const valid = lo <= pointsPerGame - 2;
    return { valid, winner: valid ? winner : null };
  }

  // hi > target → deuce extension
  // Loser must be at deuce level (>= target - 1) and winner leads by exactly 2
  if (lo < pointsPerGame - 1) {
    return { valid: false, winner: null };
  }
  const valid = hi - lo === 2;
  return { valid, winner: valid ? winner : null };
}
